import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import classNames from "classnames";
import { format, isSameDay, parse } from "date-fns";
import { kk, ru } from "date-fns/locale";
import { useField, useFormikContext } from "formik";
import { DateRange, DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import { Tooltip } from "react-tooltip";
import "./Calendar.module.css";
import { default as style, default as styles } from "./Calendar.module.css";
import { useTranslation } from "react-i18next";
import TimePicker from "../TimePiker/TImePiker";
import { log } from "node:console";


interface DateRangeObject {
  startDate: Date; // Начальная дата
  startTime: string; // Время для начальной даты
  endDate: Date; // Конечная дата
  endTime: string; // Время для конечной даты
}
export interface ICalendarProps {
  name: string;
  disabled?: boolean;
  onChange?: () => void;
  dataObject: DateRangeObject;
  setDataObject: any
}

export const CalendarSelectFormik: React.FC<ICalendarProps> = (props) => {
  const { t, i18n } = useTranslation();
  const { setFieldValue, validateField } = useFormikContext();
  const [field, meta] = useField(props.name);
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(
    field.value ? parse(field.value, "dd.MM.yyyy", new Date()) : undefined,
  );
  const [range, setRange] = useState<DateRange | undefined>(undefined);
  const handleArrowClick = () => {
    setIsTooltipOpen(!isTooltipOpen);
  };

  // Функция для изменения начальной даты
  const handleStartDateChange = (date: Date | undefined) => {
    if (date) {
      // Устанавливаем время для начальной даты на 00:00:00
      const startTime = "00:00:00";

      props.setDataObject((prev: any) => {
        const updatedObject = {
          ...prev,
          startDate: date,
          startTime: startTime,
        };

        // Если конечная дата еще не установлена, устанавливаем её на сегодняшний день с текущим временем
        if (!updatedObject.endDate) {
          const today = new Date();
          updatedObject.endDate = today;  // Сегодняшний день
          updatedObject.endTime = today.toLocaleTimeString("en-GB", {
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
          }); // Текущее время
        }

        return updatedObject;
      });
    }
  };

  



  // Функция для изменения конечной даты
  const handleEndDateChange = (date: Date | undefined) => {
    if (date) {
      // Получаем текущее время
      const currentFormattedTime = new Date().toLocaleTimeString("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });

      // Проверяем, совпадает ли выбранная дата с сегодняшним днем
      const isSameDay = (date1: Date, date2: Date) => {
        return (
          date1.getFullYear() === date2.getFullYear() &&
          date1.getMonth() === date2.getMonth() &&
          date1.getDate() === date2.getDate()
        );
      };

      // Если начальная дата не выбрана, устанавливаем её на год раньше, чем конечная дата, с временем 00:00:00
      if (!props.dataObject.startDate) {
        const startDate = new Date(date); // Берем конечную дату как базу
        startDate.setFullYear(startDate.getFullYear() - 1); // Отнимаем 1 год
        startDate.setHours(0, 0, 0, 0); // Устанавливаем время 00:00:00

        // Обновляем данные в стейте
        props.setDataObject((prev: any) => ({
          ...prev,
          startDate: startDate, // Устанавливаем начальную дату как 1 год назад
          startTime: "00:00:00", // Время на 00:00:00
          endDate: date, // Конечная дата та, которую выбрали
          endTime: isSameDay(date, new Date()) ? currentFormattedTime : "23:59:59", // Время сегодня или 23:59:59
        }));
      } else {
        // Если начальная дата уже выбрана, устанавливаем конечную дату на выбранную с текущим временем
        props.setDataObject((prev: any) => ({
          ...prev,
          endDate: date,
          endTime: isSameDay(date, new Date()) ? currentFormattedTime : "23:59:59", // Время сегодня или 23:59:59
        }));
      }
    }
  };






  const formatDateRange = () => {
    const { startDate, endDate, startTime, endTime } = props.dataObject;

    if (startDate) {
      if (!endDate) {
        return `${format(startDate, "dd.MM.yyyy")}` 
      } else {
        return `${format(startDate, "dd.MM.yyyy")}` +
          ` – ${format(endDate, "dd.MM.yyyy")}` 

      }
    }
    return "";
  };


  const handleClear = () => {
    setSelectedDate(undefined);
    setFieldValue(props.name, "", true); // Триггерим валидацию при очистке
    if (props.onChange) {
      props.onChange();
    }
    setIsTooltipOpen(false);
  };

  useEffect(() => {
    setRange({ from: props.dataObject.startDate, to: props.dataObject.endDate });
  }, [props.dataObject.startDate, props.dataObject.endDate]);

  const renderCalendars = () => (
    <div className={styles.calendarContainer}>
      {/* Календарь для начальной даты */}
      <div>
        <DayPicker
          locale={i18n.language?.includes("ru") ? ru : kk}
          mode="single"
          selected={props.dataObject.startDate}
          onSelect={(date: Date | undefined) => handleStartDateChange(date)}
          disabled={{
            after: props.dataObject.endDate || new Date(), // Дни после конечной даты или текущего дня
          }}
          className={styles.rdp}
        />

      </div>

      {/* Календарь для конечной даты */}
      <div>
        <DayPicker
          locale={i18n.language?.includes("ru") ? ru : kk}
          mode="single"
          selected={props.dataObject.endDate}
          onSelect={(date: Date | undefined) => handleEndDateChange(date)}
          disabled={{
            before: new Date(), // Дни до начальной даты

          }}
          className={styles.rdp}
        />

      </div>
    </div>
  );

  return (
    <div className={styles.inputContainer}>
      <input
        type="text"
        value={formatDateRange()}
        placeholder={formatDateRange() ? "" : "Выберите дату и время"}
        readOnly
        disabled={props.disabled}
        className={styles.field}
        data-tooltip-id={`select-date-tooltip-${props.name}`}
        onClick={handleArrowClick}
      />


      {/* {(documentStatus && (documentStatus === "DRAFT" || documentStatus === "new")) ? (
        field.value || selectedDate ? (
          <button
            type="button"
            className={styles.clearButton}
            onClick={handleClear}
          >
            &times;
          </button>
        ) : (
          <img
            className={styles.arrow}
            src="/icons/arrowDown.svg"
            onClick={handleArrowClick}
            data-tooltip-id={`select-date-tooltip-${props.name}`}
          />
        )
      ) : null} */}
      <Tooltip
        id={`select-date-tooltip-${props.name}`}
        className={classNames(style.wrapperTooltip, "drop-shadow-2xl")}
        openOnClick={true}
        clickable={true}
        noArrow={true}
        opacity={1}
        place={"bottom-end"}
        afterShow={() => setIsTooltipOpen(true)}
        afterHide={() => setIsTooltipOpen(false)}
      >
        {/* <div className="flex gap-[10px]">
          <div> */}
        {renderCalendars()}
        {/* <div className="flex flex-col gap-4 m-[1em] w-[100%]">
          <label className="flex flex-col w-full">
            <span className="mb-2 text-sm font-medium">{t("Время от")}</span>
            <TimePicker
              value={props.dataObject.startTime || "00:00:00"}
              onChange={(newTime: string) =>
                props.setDataObject((prev: any) => ({
                  ...prev,
                  startTime: newTime
                }))
              }
          />
          </label>
        </div>
          </div>
        
        <div>
        {renderCalendars()}
        <div className="flex flex-col gap-4 m-[1em] w-[100%]">

          <label className="flex flex-col w-full">
            <span className="mb-2 text-sm font-medium">{t("Время до")}</span>
            <TimePicker
              value={props.dataObject.endTime || "23:59:59"}
              onChange={(newTime: string) =>
                props.setDataObject((prev: any) => ({
                  ...prev,
                  endTime: newTime
                }))
              }
            />
          </label>
        </div>
        </div>
              
        </div> */}

      </Tooltip>

    </div>
  );
};

export default CalendarSelectFormik;
